import { BigFloat } from '@1inch-community/core/math';
import { asyncScheduler, BehaviorSubject, combineLatest, debounceTime, defer, distinctUntilChanged, firstValueFrom, map, merge, of, shareReplay, startWith, Subject, subscribeOn, Subscription, switchMap, tap, withLatestFrom, } from 'rxjs';
import { getOneInchRouterV6ContractAddress } from '../chain';
import { isTokensEqual } from '../tokens';
export class SwapContext {
    constructor(wallet, onChain, settings, pairHolder, amountDataSource, strategies) {
        this.wallet = wallet;
        this.onChain = onChain;
        this.settings = settings;
        this.pairHolder = pairHolder;
        this.amountDataSource = amountDataSource;
        this.strategies = strategies;
        this.subscription = new Subscription();
        this.loading$ = new BehaviorSubject(false);
        this.chainId$ = defer(() => this.wallet.data.chainId$).pipe(distinctUntilChanged());
        this.connectedWalletAddress$ = defer(() => this.wallet.data.activeAddress$).pipe(distinctUntilChanged());
        this.block$ = this.chainId$.pipe(switchMap((chainId) => (chainId ? this.onChain.getBlockEmitter(chainId) : of(null))));
        this.updateData$ = new Subject();
        this.updateDataComplete$ = new Subject();
        this.dataUpdateEmitter$ = merge(this.onChain.crossChainEmitter, this.connectedWalletAddress$, defer(() => this.pairHolder.streamSnapshot('source')), defer(() => this.pairHolder.streamSnapshot('destination')).pipe(map((snapshot) => snapshot.token), distinctUntilChanged(isTokensEqual)), this.updateData$).pipe(debounceTime(1000), map(() => void 0), startWith(void 0), shareReplay({ bufferSize: 1, refCount: true }));
        this.dataSnapshot$ = this.dataUpdateEmitter$.pipe(withLatestFrom(this.connectedWalletAddress$), switchMap(() => {
            this.loading$.next(true);
            return this.getDataSnapshot();
        }), tap(() => {
            this.loading$.next(false);
            this.updateDataComplete$.next();
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.rate$ = this.dataSnapshot$.pipe(map((snapshot) => snapshot?.rate ?? null));
        this.minReceive$ = this.dataSnapshot$.pipe(map((snapshot) => snapshot?.minReceive ?? BigFloat.zero()));
        this.destinationTokenAmount$ = this.dataSnapshot$.pipe(map((snapshot) => snapshot?.destinationTokenAmount ?? BigFloat.zero()));
        this.autoSlippage$ = this.dataSnapshot$.pipe(map((snapshot) => snapshot?.autoSlippage ?? null));
        this.autoAuctionTime$ = this.dataSnapshot$.pipe(map((snapshot) => snapshot?.autoAuctionTime ?? null));
        this.slippage$ = combineLatest([
            this.autoSlippage$,
            defer(() => this.settings.slippage.value$),
        ]).pipe(map(([autoSlippage, slippageSettings]) => {
            if (slippageSettings)
                return { type: slippageSettings[1], value: slippageSettings[0] };
            return { type: 'auto', value: autoSlippage };
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.auctionTime$ = combineLatest([
            this.autoAuctionTime$,
            defer(() => this.settings.auctionTime.value$),
        ]).pipe(map(([autoAuctionTime, auctionTimeSettings]) => {
            if (auctionTimeSettings) {
                return { type: auctionTimeSettings[1], value: auctionTimeSettings[0] };
            }
            return { type: 'auto', value: autoAuctionTime };
        }), shareReplay({ bufferSize: 1, refCount: true }));
    }
    init() {
        this.subscription.add(merge(this.destinationTokenAmount$.pipe(distinctUntilChanged(), tap((amount) => {
            this.setTokenAmountByType('destination', amount);
        }), subscribeOn(asyncScheduler))).subscribe());
    }
    wrapNativeToken() {
        throw new Error('Method not implemented.');
    }
    async getApprove() {
        const chainId = await this.wallet.data.getChainId();
        const sourceTokenSnapshot = this.pairHolder.getSnapshot('source');
        const owner = await this.wallet.data.getActiveAddress();
        if (!chainId || !sourceTokenSnapshot || !sourceTokenSnapshot.token || !owner) {
            throw new Error('');
        }
        const spender = getOneInchRouterV6ContractAddress(chainId);
        const result = await this.onChain.simulateApprove(chainId, sourceTokenSnapshot.token.address, owner, spender, BigFloat.maxUint256());
        return await this.wallet.writeContract(result);
    }
    getSettingsController(name) {
        const controller = this.settings[name];
        if (!controller)
            throw new Error('');
        return controller;
    }
    async getPermit() {
        // const chainId = await this.wallet.data.getChainId();
        // const walletAddress = await this.wallet.data.getActiveAddress();
        // const tokenSnapshot = this.pairHolder.getSnapshot('source');
        // if (!chainId || !walletAddress || !isTokenSnapshotNotNullable(tokenSnapshot)) {
        //   throw new Error('');
        // }
        // const signFromStorage = await getPermit(
        //   chainId,
        //   tokenSnapshot.token.address,
        //   walletAddress,
        //   getOneInchRouterV6ContractAddress(chainId)
        // );
        // if (signFromStorage !== null) return;
        // const typeData = await getPermit2TypeData(
        //   chainId,
        //   tokenSnapshot.token.address,
        //   walletAddress,
        //   getOneInchRouterV6ContractAddress(chainId)
        // );
        // const sign = await this.wallet.signTypedData(typeData);
        // await savePermit(
        //   chainId,
        //   tokenSnapshot.token.address,
        //   walletAddress,
        //   getOneInchRouterV6ContractAddress(chainId),
        //   sign,
        //   typeData.message as any
        // );
    }
    destroy() {
        this.subscription.unsubscribe();
    }
    setPair(pair) {
        this.pairHolder.setPair(pair);
    }
    setToken(tokenType, token) {
        this.pairHolder.setToken(token, tokenType);
    }
    switchPair() {
        this.pairHolder.switchPair();
    }
    async getSnapshot() {
        this.updateData$.next();
        await firstValueFrom(this.updateDataComplete$);
        const [swapSnapshot, slippage, auctionTime] = await Promise.all([
            firstValueFrom(this.dataSnapshot$),
            firstValueFrom(this.slippage$),
            firstValueFrom(this.auctionTime$),
        ]);
        return {
            ...swapSnapshot,
            slippage,
            auctionTime,
        };
    }
    async swap(swapSnapshot) {
        const pair = {
            source: swapSnapshot.sourceToken,
            destination: swapSnapshot.destinationToken,
        };
        const strategy = await this.getSupportStrategy(pair);
        return await strategy.swap(swapSnapshot);
    }
    async getMaxAmount() {
        return this.amountDataSource.getMaxAmount();
    }
    async setMaxAmount() {
        const amount = await this.getMaxAmount();
        this.setTokenAmountByType('source', amount);
    }
    getTokenByType(type) {
        return this.pairHolder.streamSnapshot(type).pipe(map((snapshot) => snapshot.token), distinctUntilChanged());
    }
    getTokenAmountByType(type) {
        return this.pairHolder.streamSnapshot(type).pipe(map((snapshot) => {
            return snapshot.amount;
        }), distinctUntilChanged());
    }
    getTokenRawAmountByType(type) {
        return this.pairHolder.streamSnapshot(type).pipe(map((snapshot) => snapshot.amount), distinctUntilChanged());
    }
    setTokenAmountByType(type, value) {
        this.pairHolder.setAmount(type, value);
    }
    async getOrderStatus() {
        throw new Error('Not implemented yet');
    }
    cancelOrder() {
        throw new Error('Not implemented yet');
    }
    async getSupportStrategy(pair) {
        const walletAddress = await this.wallet.data.getActiveAddress();
        for (const strategy of this.strategies) {
            if (await strategy.supportSwap(pair, walletAddress)) {
                return strategy;
            }
        }
        throw new Error('Supported strategy for pair not found');
    }
    async getDataSnapshot() {
        const { token: source, amount } = this.pairHolder.getSnapshot('source');
        const { token: destination } = this.pairHolder.getSnapshot('destination');
        const walletAddress = await this.wallet.data.getActiveAddress();
        if (!source || !destination || !amount) {
            return null;
        }
        for (const strategy of this.strategies) {
            try {
                return await strategy.getDataSnapshot({ source, destination }, amount, walletAddress);
            }
            catch {
                /* ignore */
            }
        }
        return null;
    }
}
//# sourceMappingURL=swap-context.js.map